import {
  DataMessageTypeEnum,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import type { ProcessedMessage } from './message-processing';

const streamedPartText = (part: TerraUIMessage['parts'][number]): string => {
  if (
    (part.type === 'text' || part.type === 'reasoning') &&
    typeof part.text === 'string'
  ) {
    return part.text.trim();
  }

  return '';
};

export function isAssistantActivelyStreaming(
  message: TerraUIMessage | undefined,
): boolean {
  if (!message || message.role !== 'assistant') {
    return false;
  }

  return message.parts.some(
    (part) =>
      (part.type === 'text' || part.type === 'reasoning') &&
      part.state === 'streaming' &&
      streamedPartText(part).length > 0,
  );
}

export function shouldShowChatGeneratingIndicator({
  isLoading,
  isWaitingForToolResult,
  lastAssistantMessage,
  isReconnecting = false,
  isCompacting = false,
}: {
  isLoading: boolean;
  isWaitingForToolResult: boolean;
  lastAssistantMessage: TerraUIMessage | undefined;
  isReconnecting?: boolean;
  isCompacting?: boolean;
}): boolean {
  if (isReconnecting) {
    return true;
  }

  if (isCompacting && isLoading) {
    return true;
  }

  if (!isLoading || isWaitingForToolResult) {
    return false;
  }

  return !isAssistantActivelyStreaming(lastAssistantMessage);
}

export function getLastAssistantMessage(messages: TerraUIMessage[]) {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];

    if (message?.role === 'assistant') {
      return message;
    }
  }

  return undefined;
}

export function hasPendingToolResult(
  processedMessages: ProcessedMessage[],
): boolean {
  const lastAssistantMessage = [...processedMessages]
    .reverse()
    .find((message) => message.message.role === 'assistant');

  return (
    lastAssistantMessage?.parts.some((processedPart) => {
      const dataMessage = processedPart.dataMessage;

      if (
        dataMessage?.type !== DataMessageTypeEnum.ToolCallStart &&
        dataMessage?.type !== DataMessageTypeEnum.ToolCallUpdate
      ) {
        return false;
      }

      return (
        dataMessage.status === 'started' || dataMessage.status === 'in-progress'
      );
    }) ?? false
  );
}
